import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { Flex, Tabs as PillTab, TabsContainer, ColorVariant, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { AppDispatch, RootState } from 'store'
import {
  getKitchenContractorOrders,
  getKitchenProduction,
  retryKitchenConsumption,
} from 'features/food/foodSlice'
import {
  FoodConsumptionRunStatus,
  FoodKitchenSupplyMode,
  IKitchenContractorOrderResponse,
  IKitchenProductionDishResponse,
  IKitchenProductionPullLineResponse,
  IKitchenProductionWindowResponse,
} from 'types/api/foodApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import { ProductionStatsRow } from '../Kitchen.styled'

const formatDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const reconStatusLabel = (status: number) => {
  if (status === FoodConsumptionRunStatus.Posted) return 'Posted'
  if (status === FoodConsumptionRunStatus.SkippedContractor) return 'Skipped (contractor)'
  if (status === FoodConsumptionRunStatus.Failed) return 'Failed'
  return 'Pending'
}

export const ProductionTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { logisticsKitchens, kitchenProduction, contractorOrders, isLoading } = useSelector(
    (store: RootState) => store.food,
  )
  const kitchens = logisticsKitchens.data || []
  const [kitchenId, setKitchenId] = useState<number | null>(null)
  const [day, setDay] = useState(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })

  useEffect(() => {
    if (kitchenId == null && kitchens[0]) setKitchenId(kitchens[0].id)
  }, [kitchens, kitchenId])

  const dayStr = formatDay(day)

  useEffect(() => {
    if (kitchenId == null) return
    dispatch(getKitchenProduction({ day: dayStr, kitchenId }))
    dispatch(getKitchenContractorOrders({ page: 1, limit: 50, kitchenId }))
  }, [dispatch, dayStr, kitchenId])

  const [retryingRunId, setRetryingRunId] = useState<number | null>(null)

  const handleRetry = async (mealWindowRunId?: number | null) => {
    if (!mealWindowRunId) return
    setRetryingRunId(mealWindowRunId)
    const resp = await dispatch(retryKitchenConsumption(mealWindowRunId))
    if (resp.meta.requestStatus === 'fulfilled' && kitchenId != null) {
      await dispatch(getKitchenProduction({ day: dayStr, kitchenId }))
    }
    setRetryingRunId(null)
  }

  const kitchen = kitchens.find((k) => k.id === kitchenId)
  const windows = useMemo(
    () => (kitchenProduction || []).filter((w) => !kitchenId || w.kitchen?.id === kitchenId),
    [kitchenProduction, kitchenId],
  )

  if (!kitchens.length) {
    return <OtherText grey>No kitchens configured yet.</OtherText>
  }

  return (
    <Flex column gap='1rem' width='100%'>
      <Flex gap='0.5rem' center width='100%' between wrap>
        <TabsContainer>
          {kitchens.map((k) => (
            <PillTab key={k.id} active={kitchenId === k.id} onClick={() => setKitchenId(k.id)}>
              {k.name}
            </PillTab>
          ))}
        </TabsContainer>
        <CalendarInput
          nomarg
          labelText='Operational day'
          defaultValue={day}
          onChange={(d: Date) => d && setDay(d)}
        />
      </Flex>

      {kitchen && kitchen.supplyMode === FoodKitchenSupplyMode.ContractorSupplied && (
        <ContractorOrdersTable orders={contractorOrders.data || []} loading={isLoading} />
      )}

      {!windows.length && <OtherText grey>No production data for this kitchen and day.</OtherText>}

      {windows.map((win, idx) => (
        <ProductionWindowCard
          key={`${win.kitchen?.id}-${win.mealWindowRun?.id || idx}`}
          win={win}
          retrying={retryingRunId === win.mealWindowRun?.id}
          onRetry={() => handleRetry(win.mealWindowRun?.id)}
        />
      ))}
    </Flex>
  )
}

const ProductionWindowCard = ({
  win,
  retrying,
  onRetry,
}: {
  win: IKitchenProductionWindowResponse
  retrying: boolean
  onRetry: () => void
}) => {
  const recon = win.reconciliation
  const pullRows = (win.pullList || []).map((p, i) => ({
    ...p,
    id: `${p.ingredient?.id || i}-${i}`,
  }))
  const dishRows = (win.dishes || []).map((d, i) => ({
    ...d,
    id: `${d.dish?.id || i}-${i}`,
  }))

  const pullColumns: IHonestTableColumn<IKitchenProductionPullLineResponse & { id: string }>[] = [
    {
      id: 'ingredient',
      header: 'Ingredient',
      cell: (r) => <BodyText>{r.ingredient?.name || '—'}</BodyText>,
    },
    { id: 'required', header: 'Required', cell: (r) => r.required },
    { id: 'available', header: 'Available', cell: (r) => r.available },
    {
      id: 'fefo',
      header: 'Pull from (FEFO)',
      cell: (r) =>
        (r.pullFrom || [])
          .map(
            (b) =>
              `#${b.batchId}${b.expiryDate ? ` · ${formatDateForDisplay(b.expiryDate)}` : ''} ×${b.quantity}`,
          )
          .join(' · ') || '—',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) =>
        r.shortfall > 0 ? (
          <StatusPill status='danger'>{`Short ${r.shortfall}`}</StatusPill>
        ) : (
          <StatusPill status='success'>OK</StatusPill>
        ),
    },
  ]

  const dishColumns: IHonestTableColumn<IKitchenProductionDishResponse & { id: string }>[] = [
    { id: 'dish', header: 'Dish', cell: (r) => <BodyText weight='600'>{r.dish?.name || '—'}</BodyText> },
    {
      id: 'tags',
      header: 'Dietary',
      cell: (r) => (r.dietaryTags || []).map((t) => t.code).join(' + ') || '—',
    },
    { id: 'portions', header: 'Portions', cell: (r) => <BodyText weight='700'>{r.portions}</BodyText> },
    { id: 'packout', header: 'Packout', cell: (r) => r.packoutPortions },
    {
      id: 'norm',
      header: 'Normative',
      cell: (r) =>
        (r.normativeVersion?.lines || [])
          .map((l) => `${l.ingredient?.name || '?'} ${l.amount}`)
          .join(' · ') || '—',
    },
  ]

  const hasShortfall = (win.pullList || []).some((p) => p.shortfall > 0)

  return (
    <Container>
      <ContainerHeader padding>
        <Flex between center width='100%' wrap gap='0.75rem'>
          <TitleText weight='700'>
            {win.mealWindowRun?.windowDef?.name || win.kitchen?.name || 'Window'}
          </TitleText>
          <Flex gap='0.5rem' wrap>
            <StatusPill status={win.menuPublished ? 'success' : 'accent'}>
              {win.menuPublished ? 'Menu published' : 'Menu draft / missing'}
            </StatusPill>
            {recon && (
              <StatusPill
                status={
                  recon.status === FoodConsumptionRunStatus.Failed
                    ? 'danger'
                    : recon.status === FoodConsumptionRunStatus.Posted ||
                        recon.status === FoodConsumptionRunStatus.SkippedContractor
                      ? 'success'
                      : 'neutral'
                }
              >
                {reconStatusLabel(recon.status)}
              </StatusPill>
            )}
          </Flex>
        </Flex>
      </ContainerHeader>
      <ContainerContent gap='1rem'>
        {win.menuMissingWarning && (
          <Alert
            title='Menu missing'
            description='Meals are planned but no menu is published for this kitchen and window.'
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        )}
        {hasShortfall && (
          <Alert
            title='Ingredient shortfall'
            description='One or more pull-list lines are short against available stock.'
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        )}
        {recon && (
          <ProductionStatsRow>
            <MetricCard value={recon.ordered} label='Ordered' accentColor={colors.orange} />
            <MetricCard value={recon.served} label='Served' accentColor={colors.orange} />
            <MetricCard value={recon.plateWaste} label='Plate waste' accentColor={colors.orange} />
            <MetricCard value={recon.standbyUsed} label='Standby used' accentColor={colors.orange} />
            <MetricCard
              value={recon.windowCost?.toFixed?.(2) ?? recon.windowCost}
              label='Window COGS'
              accentColor={colors.orange}
            />
            <MetricCard
              value={recon.costPerMeal?.toFixed?.(2) ?? recon.costPerMeal}
              label='Cost / meal'
              accentColor={colors.orange}
            />
          </ProductionStatsRow>
        )}
        {recon?.status === FoodConsumptionRunStatus.Failed && (
          <Flex gap='0.5rem' center>
            <OtherText color={colors.red}>{recon.failureReason || 'Consumption posting failed'}</OtherText>
            <Button size='sm' variant='secondary' disabled={retrying} onClick={onRetry}>
              {retrying ? 'Consumption posting queued…' : 'Retry consumption'}
            </Button>
          </Flex>
        )}
        {!!dishRows.length && (
          <>
            <BodyText weight='600'>Dishes</BodyText>
            <HonestTable columns={dishColumns} data={dishRows} keyField='id' />
          </>
        )}
        {!!pullRows.length && (
          <>
            <BodyText weight='600'>Pull list</BodyText>
            <HonestTable columns={pullColumns} data={pullRows} keyField='id' />
          </>
        )}
        {win.contractorOrder && (
          <OtherText grey>
            Contractor order #{win.contractorOrder.id} ·{' '}
            {(win.contractorOrder.lines || [])
              .map((l) => `${l.dish?.name || '—'} ×${l.qty}`)
              .join(', ')}
          </OtherText>
        )}
      </ContainerContent>
    </Container>
  )
}

const ContractorOrdersTable = ({
  orders,
  loading,
}: {
  orders: IKitchenContractorOrderResponse[]
  loading: boolean
}) => {
  const columns: IHonestTableColumn<IKitchenContractorOrderResponse>[] = [
    { id: 'id', header: 'Order', cell: (r) => `#${r.id}` },
    {
      id: 'window',
      header: 'Window',
      cell: (r) => r.mealWindowRun?.windowDef?.name || '—',
    },
    {
      id: 'lines',
      header: 'Lines',
      cell: (r) =>
        (r.lines || [])
          .map(
            (l) =>
              `${l.dish?.name || '—'} ×${l.qty} (${(l.dietaryTags || []).map((t) => t.code).join('+') || 'untagged'})`,
          )
          .join('; ') || '—',
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (r) => formatDateForDisplay(r.createdAt, true),
    },
  ]

  return (
    <Container>
      <ContainerHeader>
        <TitleText weight='700'>Contractor orders</TitleText>
      </ContainerHeader>
      <ContainerContent>
        <HonestTable
          columns={columns}
          data={orders}
          keyField='id'
          loading={loading}
          emptyMessage='No contractor orders yet'
        />
      </ContainerContent>
    </Container>
  )
}
